/**
 * Session-level benefit source (rxhub PBM member id) for the AI summary card.
 *
 * EPA / Priority Indicator / EPCS must stay hidden until a *resolved* PBM is
 * chosen this session (`SELECT_BENEFIT_SOURCE` is not resolved). The store is
 * keyed by patient and subscribed by every mounted PrescriptionSummary so a
 * later PBM pick can reveal flags on earlier cards without rewriting the Rx.
 */

export const SELECT_BENEFIT_SOURCE = "SELECT_BENEFIT_SOURCE";

type Listener = () => void;

let store: Record<string, string> = {};
const listeners = new Set<Listener>();

const notify = (): void => {
  listeners.forEach((cb) => cb());
};

export const isResolvedBenefitSource = (id: string | null | undefined): boolean => {
  const v = (id || "").trim();
  return Boolean(v) && v.toUpperCase() !== SELECT_BENEFIT_SOURCE;
};

export const subscribeSessionBenefitSource = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getSessionBenefitSource = (patientId: string): string =>
  store[patientId || ""] || "";

export const setSessionBenefitSource = (patientId: string, rxhubId: string): void => {
  const p = patientId || "";
  const v = (rxhubId || "").trim();
  if (!isResolvedBenefitSource(v)) return;
  if (store[p] === v) return;
  store[p] = v;
  notify();
};

/** Apply a WS result's session PBM if present; ignore missing/sentinel so other turns don't wipe a pick. */
export const applySessionBenefitSourceFromResult = (
  patientId: string,
  data: unknown
): void => {
  if (!data || typeof data !== "object") return;
  const d = data as Record<string, unknown>;
  const direct = typeof d.rxhub_pbm_unique_id === "string" ? d.rxhub_pbm_unique_id : "";
  const selected =
    d.selected_pbm && typeof d.selected_pbm === "object"
      ? (d.selected_pbm as Record<string, unknown>).memberUniqueId
      : "";
  const nestedDi =
    d.drug_interaction_response && typeof d.drug_interaction_response === "object"
      ? (d.drug_interaction_response as Record<string, unknown>).rxhub_pbm_unique_id
      : "";
  const candidate = [direct, selected, nestedDi]
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .find((x) => isResolvedBenefitSource(x));
  if (candidate) {
    setSessionBenefitSource(patientId, candidate);
  }
};

export const clearSessionBenefitSource = (): void => {
  store = {};
  notify();
};
